<template>
    <el-dialog
        :title="dialogTitle"
        :visible="visible"
        width="680px"
        top="5vh"
        append-to-body
        custom-class="rf-dialog"
        :close-on-click-modal="false"
        @update:visible="(v) => $emit('update:visible', v)"
        @closed="onClosed"
    >
        <div v-loading="loading" data-testid="rf-dialog">
            <!-- ลิงก์และ QR: the whole body after create; always on top in edit -->
            <div v-if="code" class="rf-link-card mb-3" data-testid="rf-link-card">
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <h4 class="mb-0">{{ justCreated ? code.name || code.code : $t("marketing.referral.link_card") }}</h4>
                    <el-tag size="small" :type="stateTag(code.state)">{{ $t("marketing.referral.state." + code.state) }}</el-tag>
                </div>
                <div class="rf-link-row">
                    <div class="rf-qr">
                        <img v-if="qrUrl" :src="qrUrl" :alt="code.code" width="132" height="132" data-testid="rf-qr" />
                        <div v-else class="rf-qr-empty"><i class="fa fa-qrcode"></i></div>
                    </div>
                    <div class="rf-link-body">
                        <div class="rf-code">{{ code.code }}</div>
                        <el-input :value="code.link" readonly size="small" class="mt-1" data-testid="rf-link"></el-input>
                        <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                            <base-button size="sm" type="primary" data-testid="rf-copy" @click="copyLink"><i class="fa fa-copy"></i> {{ $t("marketing.referral.copy_link") }}</base-button>
                            <base-button size="sm" type="secondary" :loading="downloading" data-testid="rf-download-qr" @click="downloadQr"><i class="fa fa-download"></i> {{ $t("marketing.referral.download_qr") }} (PNG)</base-button>
                        </div>
                        <small class="d-block text-muted mt-2">{{ $t("marketing.referral.hint.link", { landing: code.landing_path || "/" }) }}</small>
                    </div>
                </div>
            </div>

            <template v-if="!justCreated">
                <el-alert v-if="staleError" type="warning" :closable="false" show-icon class="mb-3" :title="$t('marketing.referral.err.stale')">
                    <base-button size="sm" type="secondary" class="mt-1" @click="reloadCode">{{ $t("marketing.referral.reload") }}</base-button>
                </el-alert>

                <!-- 1. code -->
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.field.code") }}</label>
                    <template v-if="isEdit">
                        <el-input :value="code && code.code" readonly class="rf-mono"></el-input>
                        <small class="text-muted">{{ $t("marketing.referral.code_readonly") }}</small>
                    </template>
                    <template v-else>
                        <div class="rf-code-row">
                            <el-input v-model="codeInput" class="rf-mono rf-code-input" maxlength="24" :placeholder="'AB12CD'" data-testid="rf-code" @input="onCodeInput"></el-input>
                            <el-input v-model="prefix" class="rf-mono rf-prefix" maxlength="8" :placeholder="$t('marketing.referral.field.prefix')" data-testid="rf-prefix" @input="prefix = clean(prefix)"></el-input>
                            <base-button size="sm" type="secondary" class="rf-generate" data-testid="rf-generate" @click="generate"><i class="fa fa-random"></i> {{ $t("marketing.referral.generate") }}</base-button>
                        </div>
                        <small class="d-block" :class="codeStatusClass" data-testid="rf-code-status">{{ codeStatusText }}</small>
                        <small v-if="errors.prefix" class="d-block text-danger">{{ $t("marketing.referral.prefix_hint") }}</small>
                    </template>
                </div>

                <!-- 2. name -->
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.field.name") }}</label>
                    <el-input v-model="form.name" maxlength="120" :disabled="!canWrite" data-testid="rf-name"></el-input>
                    <small v-if="errors.name" class="text-danger">{{ err(errors.name) }}</small>
                </div>

                <!-- 3. owner -->
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.referral.field.owner") }}</label>
                    <el-select
                        v-model="form.owner_bo_user_id"
                        class="w-100"
                        filterable
                        remote
                        clearable
                        :remote-method="searchOwners"
                        :loading="ownersLoading"
                        :disabled="!canWrite"
                        :placeholder="$t('marketing.referral.owner_placeholder')"
                        data-testid="rf-owner"
                        @visible-change="(o) => o && !owners.length && searchOwners('')"
                    >
                        <el-option v-for="o in owners" :key="o.id" :value="o.id" :label="ownerLabel(o)"></el-option>
                    </el-select>
                    <small v-if="errors.owner_bo_user_id" class="text-danger">{{ err(errors.owner_bo_user_id) }}</small>
                </div>

                <!-- 4–10 -->
                <referral-shared-fields
                    :form="form"
                    :errors="errors"
                    :teams="teams"
                    :campaigns="campaigns"
                    :lps="lps"
                    :templates="templates"
                    :original="isEdit ? code : null"
                    :disabled="!canWrite"
                ></referral-shared-fields>
            </template>
        </div>

        <span slot="footer">
            <template v-if="justCreated">
                <base-button type="primary" size="sm" data-testid="rf-done" @click="close">{{ $t("marketing.referral.done") }}</base-button>
            </template>
            <template v-else>
                <base-button type="secondary" size="sm" @click="close">{{ $t("marketing.referral.cancel") }}</base-button>
                <base-button v-if="canWrite" type="primary" size="sm" :loading="saving" data-testid="rf-save" @click="save">{{ $t("marketing.referral.save") }}</base-button>
            </template>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, Select, Option, Tag, Alert, Input, Loading } from "element-ui";
import { ReferralCodeService, LandingPageService } from "@/resource";
import { apiErrorCode, isSchemaMissing } from "@/utils/marketing";
import { copyToClipboard } from "@/utils/clipboard";
import {
    cleanReferralInput,
    codeInputPayload,
    emptyCodeForm,
    fieldErrors,
    formFromCode,
    normalizeReferralCode,
    randomReferralCode,
    referralStateTag,
    validateCodeForm,
    withIsoDates
} from "@/utils/referral";
import ReferralSharedFields from "./ReferralSharedFields.vue";

// การตลาด → รหัสแนะนำ — create / edit dialog (Phase 2 spec 2026-10-13 §5.3).
export default {
    name: "ReferralCodeDialog",
    directives: { loading: Loading.directive },
    components: {
        [Dialog.name]: Dialog,
        [Select.name]: Select,
        [Option.name]: Option,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [Input.name]: Input,
        ReferralSharedFields
    },
    props: {
        visible: { type: Boolean, default: false },
        codeId: { type: [Number, String], default: null },
        canWrite: { type: Boolean, default: false }
    },
    data() {
        return {
            loading: false,
            saving: false,
            downloading: false,
            code: null,
            justCreated: false,
            form: emptyCodeForm(),
            codeInput: "",
            prefix: "",
            codeState: { status: "", seq: 0 },
            errors: {},
            staleError: false,
            owners: [],
            ownersLoading: false,
            teams: [],
            campaigns: [],
            lps: [],
            templates: [],
            qrUrl: "",
            checkTimer: null
        };
    },
    computed: {
        isEdit() {
            return !!this.codeId;
        },
        dialogTitle() {
            if (this.justCreated) return this.$t("marketing.referral.link_card");
            return this.isEdit ? this.$t("marketing.referral.edit") : this.$t("marketing.referral.create");
        },
        codeStatusText() {
            const s = this.codeState.status;
            if (!s) return this.$t("marketing.referral.code_blank_hint");
            return this.$t("marketing.referral.code_status." + s);
        },
        codeStatusClass() {
            return { available: "text-success", taken: "text-danger", format: "text-danger" }[this.codeState.status] || "text-muted";
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(v) {
                if (v) this.open();
            }
        }
    },
    beforeDestroy() {
        this.revokeQr();
        clearTimeout(this.checkTimer);
    },
    methods: {
        stateTag: referralStateTag,
        clean: cleanReferralInput,
        err(rule) {
            const k = "marketing.referral.err." + rule;
            return this.$te(k) ? this.$t(k) : this.$t("marketing.referral.err.format");
        },
        ownerLabel(o) {
            return o.nick_name ? `${o.name} (${o.nick_name})` : o.name;
        },
        async open() {
            this.reset();
            this.loadOptions();
            if (!this.isEdit) return;
            this.loading = true;
            try {
                await this.reloadCode();
            } finally {
                this.loading = false;
            }
        },
        reset() {
            this.revokeQr();
            this.code = null;
            this.justCreated = false;
            this.form = emptyCodeForm();
            this.codeInput = "";
            this.prefix = "";
            this.codeState = { status: "", seq: 0 };
            this.errors = {};
            this.staleError = false;
        },
        async reloadCode() {
            try {
                const res = await ReferralCodeService.get(this.codeId);
                this.setCode((res.data && res.data.data) || null);
                this.form = formFromCode(this.code);
                this.staleError = false;
                this.errors = {};
                if (this.code && this.code.owner_bo_user_id && !this.owners.some((o) => o.id === this.code.owner_bo_user_id)) {
                    this.owners = [{ id: this.code.owner_bo_user_id, name: this.code.owner_name || "#" + this.code.owner_bo_user_id, nick_name: "" }].concat(this.owners);
                }
            } catch (err) {
                this.notifyError(err);
            }
        },
        setCode(c) {
            this.code = c;
            this.loadQr();
        },
        async loadOptions() {
            const settle = (p) => p.then((r) => (r && r.data && r.data.data) || null).catch(() => null);
            const [meta, templates, lps] = await Promise.all([
                settle(ReferralCodeService.meta()),
                settle(ReferralCodeService.couponTemplates()),
                settle(LandingPageService.list({ status: "live", page: 1, limit: 100 }))
            ]);
            this.teams = (meta && meta.teams) || [];
            this.campaigns = (meta && meta.campaigns) || [];
            this.templates = templates || [];
            this.lps = (lps || []).filter((lp) => lp && lp.slug);
        },
        async searchOwners(q) {
            this.ownersLoading = true;
            try {
                const res = await ReferralCodeService.owners(q);
                const found = (res.data && res.data.data) || [];
                const keep = this.owners.filter((o) => o.id === this.form.owner_bo_user_id && !found.some((f) => f.id === o.id));
                this.owners = keep.concat(found);
            } catch (e) {
                /* keep the current list */
            } finally {
                this.ownersLoading = false;
            }
        },
        onCodeInput() {
            this.codeInput = cleanReferralInput(this.codeInput);
            if (this.errors.code) {
                const e = { ...this.errors };
                delete e.code;
                this.errors = e;
            }
            this.checkCode();
        },
        generate() {
            this.codeInput = randomReferralCode(this.prefix);
            this.checkCode();
        },
        checkCode() {
            clearTimeout(this.checkTimer);
            const c = this.codeInput;
            const seq = this.codeState.seq + 1;
            if (!c) {
                this.codeState = { status: "", seq };
                return;
            }
            if (!normalizeReferralCode(c)) {
                this.codeState = { status: "format", seq };
                return;
            }
            this.codeState = { status: "checking", seq };
            this.checkTimer = setTimeout(async () => {
                try {
                    const res = await ReferralCodeService.checkCode(c);
                    const d = (res.data && res.data.data) || {};
                    if (seq !== this.codeState.seq) return;
                    this.codeState = { status: d.available ? "available" : d.reason === "format" ? "format" : "taken", seq };
                } catch (e) {
                    // A failing check never blocks: the POST answers 409 code_taken anyway.
                    if (seq === this.codeState.seq) this.codeState = { status: "", seq };
                }
            }, 300);
        },
        async save() {
            const f = withIsoDates(this.form);
            const opts = this.isEdit
                ? { mode: "edit", usedCount: this.code ? this.code.used_count : 0 }
                : { mode: "create", codeMode: this.codeInput ? "typed" : "generate", code: this.codeInput, prefix: this.prefix };
            this.errors = validateCodeForm(f, opts);
            if (!this.isEdit && this.codeState.status === "taken") this.errors = { ...this.errors, code: "code_taken" };
            if (Object.keys(this.errors).length) return;
            const payload = codeInputPayload(f);
            this.saving = true;
            try {
                let res;
                if (this.isEdit) {
                    res = await ReferralCodeService.update(this.code.id, { ...payload, expected_updated_at: this.code.updated_at });
                } else {
                    res = await ReferralCodeService.create(this.codeInput ? { ...payload, code: this.codeInput } : { ...payload, prefix: this.prefix });
                }
                const saved = (res.data && res.data.data) || null;
                this.$emit("saved", saved);
                if (this.isEdit) {
                    this.$notify({ type: "success", message: this.$t("marketing.referral.saved") });
                    this.setCode(saved);
                    this.form = formFromCode(saved);
                } else {
                    this.$notify({ type: "success", message: this.$t("marketing.referral.created") });
                    this.justCreated = true;
                    this.setCode(saved);
                }
            } catch (err) {
                this.handleSaveError(err);
            } finally {
                this.saving = false;
            }
        },
        handleSaveError(err) {
            const code = apiErrorCode(err);
            if (code === "invalid_body") {
                this.errors = fieldErrors(err);
                if (this.errors.code) this.codeState = { status: "format", seq: this.codeState.seq + 1 };
                return;
            }
            if (code === "code_taken") {
                this.codeState = { status: "taken", seq: this.codeState.seq + 1 };
                return;
            }
            if (code === "invalid_landing") {
                this.errors = { landing_path: "invalid_landing" };
                return;
            }
            if (code === "template_invalid") {
                this.errors = { reward_coupon_template_id: "template_invalid" };
                return;
            }
            if (code === "stale") {
                this.staleError = true;
                return;
            }
            this.notifyError(err);
        },
        notifyError(err) {
            const code = apiErrorCode(err);
            let msg = this.$t("marketing.referral.err.generic");
            if (isSchemaMissing(err)) msg = this.$t("marketing.schema_missing");
            else if (code && this.$te("marketing.referral.err." + code)) msg = this.$t("marketing.referral.err." + code);
            this.$notify({ type: "danger", message: msg });
        },
        async copyLink() {
            const ok = await copyToClipboard(this.code && this.code.link);
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.referral.copied") : this.$t("marketing.referral.err.generic") });
        },
        revokeQr() {
            if (this.qrUrl) URL.revokeObjectURL(this.qrUrl);
            this.qrUrl = "";
        },
        async loadQr() {
            this.revokeQr();
            if (!this.code || !this.code.id) return;
            const id = this.code.id;
            try {
                const res = await ReferralCodeService.qrBlob(id, { size: 512 });
                if (!this.code || this.code.id !== id || !(res.data instanceof Blob)) return;
                this.qrUrl = URL.createObjectURL(res.data);
            } catch (e) {
                this.qrUrl = "";
            }
        },
        async downloadQr() {
            if (!this.code) return;
            this.downloading = true;
            try {
                const res = await ReferralCodeService.qrBlob(this.code.id, { size: 1024, download: true });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `referral-${this.code.code}.png`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.downloading = false;
            }
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            this.revokeQr();
        }
    }
};
</script>

<style scoped>
.rf-link-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px 16px;
    background: #f8f9fe;
}
.rf-link-row {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}
.rf-qr img,
.rf-qr-empty {
    width: 132px;
    height: 132px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #fff;
    display: block;
}
.rf-qr-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: #6c757d;
}
.rf-link-body {
    flex: 1 1 auto;
    min-width: 0;
}
.rf-code {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: 0.08em;
}
.rf-code-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.rf-code-input {
    flex: 1 1 auto;
}
.rf-prefix {
    flex: 0 0 130px;
}
.rf-generate {
    flex: 0 0 auto;
    margin: 0;
}
.rf-mono >>> input {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.06em;
}
</style>
